// Detects major food allergens (the US "top 9": milk, eggs, fish, crustacean
// shellfish, tree nuts, peanuts, wheat, soy, sesame) in an ingredient's name and
// open-text sub-ingredients, and rolls them up across a recipe.
//
// Detection is keyword based, so it only knows what the text says: an
// ingredient like "natural flavor" that hides milk won't be flagged. For those,
// an ingredient's manually entered `allergens` overrides detection.
// Frontend mirror of /backend/recipes/shared/allergens.ts; keep the two in sync.

export const MAJOR_ALLERGENS = [
  'MILK',
  'EGGS',
  'FISH',
  'SHELLFISH',
  'TREE NUTS',
  'PEANUTS',
  'WHEAT',
  'SOY',
  'SESAME',
] as const

export type Allergen = (typeof MAJOR_ALLERGENS)[number]

// Phrases where an allergen-looking word isn't that allergen (cocoa butter
// isn't milk, rice flour isn't wheat). The misleading word is dropped and the
// rest kept, so "peanut butter" still reads as peanut and "soy milk" as soy.
const FALSE_POSITIVES: [RegExp, string][] = [
  [/\b(COCOA|CACAO|SHEA|PEANUT|ALMOND|CASHEW|NUT|APPLE|PUMPKIN|SUNFLOWER|SEED|COCONUT|MANGO|KOKUM)\s+BUTTER\b/g, '$1'],
  [/\b(COCONUT|ALMOND|CASHEW|OAT|RICE|SOY|SOYA|HEMP)\s+(MILK|CREAM)\b/g, '$1'],
  [/\bCREAM\s+OF\s+TARTAR\b/g, ' '],
  [/\b(RICE|CORN|OAT|ALMOND|COCONUT|TAPIOCA|POTATO|SOY|SOYA|CHICKPEA|GARBANZO|CASSAVA|SORGHUM|MILLET|BUCKWHEAT|QUINOA|PEANUT)\s+FLOUR\b/g, '$1'],
]

const PATTERNS: Record<Allergen, RegExp> = {
  MILK: /\b(MILK|MILKFAT|CREAM|BUTTER|BUTTERMILK|BUTTERFAT|WHEY|CASEIN|CASEINATES?|CHEESES?|YOG(H)?URT|LACTOSE|GHEE|CURDS?|LACTALBUMIN|LACTOGLOBULIN|KEFIR)\b/,
  EGGS: /\b(EGGS?|EGG\s+(WHITES?|YOLKS?)|ALBUMEN|ALBUMIN|MERINGUE|MAYONNAISE)\b/,
  FISH: /\b(FISH|ANCHOV(Y|IES)|COD|SALMON|TUNA|TILAPIA|POLLOCK|HADDOCK|HALIBUT|SARDINES?|TROUT|CATFISH|MACKEREL|SNAPPER|SWORDFISH|MAHI)\b/,
  SHELLFISH: /\b(SHELLFISH|SHRIMP|PRAWNS?|CRABS?|LOBSTERS?|CRAWFISH|CRAYFISH|LANGOUSTINES?|KRILL)\b/,
  'TREE NUTS': /\b(TREE\s+NUTS?|ALMONDS?|PECANS?|WALNUTS?|CASHEWS?|PISTACHIOS?|HAZELNUTS?|FILBERTS?|MACADAMIAS?|BRAZIL\s+NUTS?|PINE\s+NUTS?|CHESTNUTS?|PRALINES?|MARZIPAN)\b/,
  PEANUTS: /\b(PEANUTS?|GROUNDNUTS?|ARACHIS)\b/,
  WHEAT: /\b(WHEAT|FLOUR|SEMOLINA|DURUM|SPELT|FARINA|BULGUR|COUSCOUS|SEITAN|EINKORN|EMMER|KAMUT|GRAHAM)\b/,
  SOY: /\b(SOY|SOYA|SOYBEANS?|TOFU|EDAMAME|MISO|TEMPEH|TAMARI|SHOYU)\b/,
  SESAME: /\b(SESAME|TAHINI|BENNE)\b/,
}

// Tree nuts are declared with the nut type, e.g. "TREE NUTS (PECANS)".
const TREE_NUT_TYPES: [RegExp, string][] = [
  [/\bALMONDS?\b/, 'ALMONDS'],
  [/\bBRAZIL\s+NUTS?\b/, 'BRAZIL NUTS'],
  [/\bCASHEWS?\b/, 'CASHEWS'],
  [/\bCHESTNUTS?\b/, 'CHESTNUTS'],
  [/\b(HAZELNUTS?|FILBERTS?)\b/, 'HAZELNUTS'],
  [/\bMACADAMIAS?\b/, 'MACADAMIAS'],
  [/\b(PECANS?|PRALINES?)\b/, 'PECANS'],
  [/\bPINE\s+NUTS?\b/, 'PINE NUTS'],
  [/\bPISTACHIOS?\b/, 'PISTACHIOS'],
  [/\bWALNUTS?\b/, 'WALNUTS'],
]

export interface AllergenSource {
  name?: string | null
  sub_ingredients?: string | null
  /** Manually entered allergens; when set, used instead of detection. */
  allergens?: string | null
}

export interface AllergenResult {
  allergens: Allergen[]
  treeNuts: string[]
}

function scan(text: string): AllergenResult {
  let t = text.toUpperCase()
  for (const [re, keep] of FALSE_POSITIVES) t = t.replace(re, keep)
  const allergens = MAJOR_ALLERGENS.filter(a => PATTERNS[a].test(t))
  const treeNuts = allergens.includes('TREE NUTS')
    ? TREE_NUT_TYPES.filter(([re]) => re.test(t)).map(([, label]) => label)
    : []
  return { allergens, treeNuts }
}

/** Allergens detected in one ingredient's name and sub-ingredients. */
export function autoDetectAllergens(source: AllergenSource): AllergenResult {
  return scan(`${source.name ?? ''}, ${source.sub_ingredients ?? ''}`)
}

/** True when the ingredient has manually entered allergens. */
export function hasManualAllergens(source: AllergenSource): boolean {
  return (source.allergens ?? '').trim() !== ''
}

/**
 * An ingredient's allergens: its manual entry when set (e.g. "MILK, SOY", or
 * "NONE" to clear a false detection), otherwise what's detected.
 */
export function detectAllergens(source: AllergenSource): AllergenResult {
  return hasManualAllergens(source)
    ? scan(source.allergens ?? '')
    : autoDetectAllergens(source)
}

/** Union of allergens across a recipe's ingredients, in canonical order. */
export function rollUpAllergens(sources: AllergenSource[]): AllergenResult {
  const found = new Set<Allergen>()
  const nuts = new Set<string>()
  for (const s of sources) {
    const r = detectAllergens(s)
    r.allergens.forEach(a => found.add(a))
    r.treeNuts.forEach(n => nuts.add(n))
  }
  return {
    allergens: MAJOR_ALLERGENS.filter(a => found.has(a)),
    treeNuts: [...nuts].sort(),
  }
}

/** Formats allergens for a product, e.g. "MILK, SOY, TREE NUTS (PECANS)". */
export function formatAllergens(result: AllergenResult): string {
  return result.allergens
    .map(a =>
      a === 'TREE NUTS' && result.treeNuts.length > 0
        ? `TREE NUTS (${result.treeNuts.join(', ')})`
        : a,
    )
    .join(', ')
}
